/**
 * ExternalRelease detail component for viewing release information.
 */

import { useEffect, useRef, useState } from 'react';
import { Descriptions, Tag, Button, Space, Card, Typography, Divider, Progress, Tooltip, Modal, Form, Input, message } from 'antd';
import {
  DownloadOutlined,
  CopyOutlined,
  EditOutlined,
  HistoryOutlined,
  FileOutlined,
  ReloadOutlined,
  CloudUploadOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import type {
  ExternalReleaseDetail as ExternalReleaseDetailType,
  ExternalReleaseFile,
  RpaCheck,
  ThirdPartyUploadTriggerResult,
} from '@/types';
import { formatDate, formatFileSize, formatUploadMode, formatChunkSize, copyToClipboard } from '@/utils';
import { formatVehicleList, toVehicleList } from '@/utils/vehicle';
import { resolveUploadPercent } from '@/utils/uploadProgress';
import { releasePurposeLabel, versionTypeLabel } from '@/constants/externalRelease';

const formatUploadTiming = (startedAt: string, finishedAt: string, avgSpeedMbps?: number | null): string => {
  const fmt = (d: Date) =>
    `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  const start = new Date(startedAt);
  const end = new Date(finishedAt);
  const durationSec = Math.round((end.getTime() - start.getTime()) / 1000);
  const speed = avgSpeedMbps != null ? `, ${avgSpeedMbps.toFixed(1)}MB/s` : '';
  return `${fmt(start)}~${fmt(end)}, ${durationSec}s${speed}`;
};
import { downloadFile, uploadFile, getShareableDownloadLink } from '@/services/obsService';
import { completeExternalReleaseFileUpload, failExternalReleaseFileUpload, heartbeatExternalReleaseFile, patchExternalRelease } from '@/api/externalRelease';
import { listUploadRecords, updateUploadRecord } from '@/api/uploadRecords';
import type { UploadedFileItem } from '@/api/uploadRecords';
import { reportRetryUploadRecord } from '@/utils/uploadRecordUtils';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { useThirdPartyUploadStore } from '@/store/thirdPartyUploadStore';
import { triggerUpload, retryTask, getRpaCheckFromError, getExistingFilesFromError } from '@/api/thirdPartyUpload';
import type { ExistingFilesCheck } from '@/api/thirdPartyUpload';
import { JlrAuthModal } from '@/components/packages/JlrAuthModal';
import type { JlrAuthPayload } from '@/components/packages/JlrAuthModal';
import { CopyExternalLinkButton } from '@/components/packages/CopyExternalLinkButton';
import { FeishuRichText } from '@/components/common';
import { useFeishuTitles } from '@/hooks/useFeishuTitles';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/i18n';

const { Text, Title } = Typography;

const formatSpeed = (bps: number) =>
  bps > 1024 * 1024
    ? `${(bps / 1024 / 1024).toFixed(1)} MB/s`
    : `${(bps / 1024).toFixed(0)} KB/s`;

interface ExternalReleaseDetailProps {
  release: ExternalReleaseDetailType;
  onEdit?: () => void;
  onViewLogs?: () => void;
  onClose?: () => void;
  onRefresh?: () => void;
}

/** RPA 校验的二次确认（force = 用户点了「仍然上传」） */
type TpConfirm = { check_id: number; decision: 'force' | 'cancel' };

/** 触发第三方上传时可能带上的目标信息 */
interface TpRunOptions {
  targetDir?: string;
  /** 交互认证插件（JLR SharePoint）的三项，来自认证弹窗 */
  auth?: JlrAuthPayload;
  /** 用户在同名文件弹窗里选了「覆盖」 */
  overwrite?: boolean;
  /** 用户在同名文件弹窗里选了「只上传不同名的文件」 */
  skipExisting?: boolean;
}

export const ExternalReleaseDetail: React.FC<ExternalReleaseDetailProps> = ({
  release,
  onEdit,
  onViewLogs,
  onClose,
  onRefresh,
}) => {
  const { t, lang } = useT();
  const { progress, setProgress, incrementAutoRetry, incrementManualRetry } = useUploadProgressStore();
  // 外部用户对该页面只读：所有写操作入口置灰
  const { isExternal } = useAuth();
  const canWrite = !isExternal;

  // 文档「内容」字段里可能内嵌飞书链接，解析标题后展示
  const { titles: feishuTitles, loading: feishuLoading } = useFeishuTitles([
    release.momenta_release_note_content,
    release.momenta_test_report_content,
  ]);

  // ---- 第三方上传（Feature 007）----------------------------------------
  const tpByRelease = useThirdPartyUploadStore((s) => s.byRelease);
  const tpStartPolling = useThirdPartyUploadStore((s) => s.startPolling);
  const tpStopPolling = useThirdPartyUploadStore((s) => s.stopPolling);
  const tpState = tpByRelease[release.id];
  const tpTasks = tpState?.tasks ?? [];
  // 重试/重新上传都是**新建任务行**，同一个文件可能有多条历史。只认 id 最大的那条
  // （= 最后一次尝试），否则重试成功后这一行还显示着上一轮的失败。
  const tpLatestTasks = (() => {
    const latest = new Map<number | null, (typeof tpTasks)[number]>();
    for (const t of tpTasks) {
      const current = latest.get(t.file_id);
      if (!current || t.id > current.id) latest.set(t.file_id, t);
    }
    return [...latest.values()];
  })();
  // 整包任务（file_id=null）不对应任何文件行，必须先摘出去单独渲染：
  // 留在表里会以 null 为键塌成一条，逐文件行和它互相都查不到。
  const tpPackageTask = tpLatestTasks.find((t) => t.file_id === null) ?? null;
  // 其余任务按文件索引，逐文件行直接查表（FR-024）
  const tpTaskByFile = new Map(
    tpLatestTasks.filter((t) => t.file_id !== null).map((t) => [t.file_id, t])
  );
  const [tpTriggering, setTpTriggering] = useState(false);
  // 目标目录弹窗（Feature 009）：插件要求（BMW）或支持可选目录（德赛）时走这条路
  const [tpDirForm] = Form.useForm();
  const [tpDirOpen, setTpDirOpen] = useState(false);
  const [tpDirSubmitting, setTpDirSubmitting] = useState(false);
  // 交互认证弹窗（JLR SharePoint）：账号密码 → Authenticator 号码匹配 → 确认
  const [tpAuthOpen, setTpAuthOpen] = useState(false);
  // 目标文件夹已有同名文件：等用户三选一（覆盖 / 只传不同名的 / 取消）
  const [tpConflict, setTpConflict] = useState<
    (ExistingFilesCheck & {
      opts: TpRunOptions;
      /** 若这次上传是在「RPA 校验未通过」之后强制的，重试时必须原样带上 */
      confirm?: TpConfirm;
    }) | null
  >(null);

  const tpPlugin = release.project?.third_party_plugin ?? null;
  const tpNeedsTargetDir = tpPlugin?.requires_target_dir === true;
  // 需要用户当场完成 Authenticator 批准的插件：走认证弹窗，而不是目录弹窗
  const tpNeedsAuth = tpPlugin?.requires_interactive_auth === true;
  // 可选目录（德赛白云山/白波山/雾灵山）：可填可不填，留空=平台默认/分享根目录。
  // 武当山 P59A 的目录由平台固定计划决定，不展示这个输入。
  const tpPrimaryVehicle = toVehicleList(release.vehicle_model)[0] ?? '';
  const tpOptionalTargetDir =
    tpPlugin?.uses_target_dir === true && tpPrimaryVehicle !== 'P59A';
  const tpAsksTargetDir = tpNeedsTargetDir || tpOptionalTargetDir;
  const tpPackageReady = release.upload_status === 'done';
  const tpUploading = (tpState?.aggregateStatus ?? release.third_party_status) === 'uploading';
  // editable 以轮询到的实时值为准，未拉到时退回列表接口给的快照
  const tpEditable = tpState?.editable ?? release.third_party_editable ?? true;

  // 车型决定第三方平台上的一级目录，后端会硬性拒绝为空的情况。前置提示，
  // 免得用户点了才知道。（对逐文件插件其实不必需，但接口没暴露插件是否为
  // 整包模式，统一提示比漏拦好。）
  const tpDisabledReason = !tpPlugin
    ? t('当前项目空间未配置第三方插件，请联系管理员在项目配置中选择')
    : toVehicleList(release.vehicle_model).length === 0
      ? t('产物包未填写车型，无法确定第三方平台存放目录')
      : !tpPackageReady
        ? t('产物包尚未上传完成，完成后才能上传第三方平台')
        : tpUploading
          ? t('正在上传第三方平台，请等待本次上传完成')
          : '';

  // 进入详情即拉一次：包含「上次上传的历史结果」，不只在上传中才有数据。
  // 已有未终态任务时 startPolling 会持续轮询，全部终态后自行停止（FR-025）。
  useEffect(() => {
    tpStartPolling(release.id);
    return () => tpStopPolling(release.id);
  }, [release.id, tpStartPolling, tpStopPolling]);

  const errorMessage = (e: unknown) =>
    (e as { response?: { data?: { message?: string } } }).response?.data?.message;

  const reportUploadAccepted = (res: ThirdPartyUploadTriggerResult) => {
    // 整包模式只会建 1 条任务，「已提交 1 个文件」是错的说法
    const isPackage = res.tasks.some((t) => t.file_id === null);
    if (res.created_count > 0) {
      message.success(
        isPackage ? t('已提交产线包构建与上传') : t('已提交 {count} 个文件到第三方平台', { count: res.created_count })
      );
    } else if (res.forced) {
      // 弹窗期间已被他人触发（Feature 008 边界：弹窗期间状态变化）
      message.warning(t('该产物包已在上传中，未重复提交'));
    } else {
      message.info(
        isPackage ? t('本次上传已在进行中或已完成，未重复提交')
          : t('所有文件均已在队列中或已上传成功，未重复提交')
      );
    }
    tpStartPolling(release.id);
  };

  /** RPA 校验未通过：由用户决定是否强制上传。取消 / 关闭窗口都尽力上报 cancel，失败静默 */
  const confirmRpaCheck = (check: RpaCheck, opts: TpRunOptions = {}) => {
    const cancel = () => {
      triggerUpload(release.id, { confirm: { check_id: check.id, decision: 'cancel' } })
        .catch(() => undefined);
    };
    Modal.confirm({
      title: t('RPA 校验未通过'),
      content: check.reason || t('RPA 未允许上传，未提供原因'),
      okText: t('仍然上传'),
      okButtonProps: { danger: true },
      cancelText: t('取消'),
      onCancel: cancel,
      onOk: async () => {
        const force = { check_id: check.id, decision: 'force' as const };
        try {
          // 强制上传必须原样带上目录/认证会话：后端每次都校验，漏传会被 400 或 409
          // 拦下，用户以为点了「仍然上传」却没动静（FR-022a）。
          const res = await triggerWithOptions(opts, force);
          reportUploadAccepted(res);
        } catch (e) {
          // 强制上传同样可能被同名文件拦下（真实踩过：RPA 校验每次都异常 → 永远走这条
          // 分支 → 409 只弹了一条 toast，用户没有覆盖/跳过的选择权）。
          const conflicts = getExistingFilesFromError(e);
          if (conflicts) {
            confirmOverwrite(conflicts, opts, force);
            return;
          }
          const code = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
          message.error(
            code === 'rpa_confirm_invalid'
              ? t('校验结果已失效，请重新点击')
              : errorMessage(e) || t('提交第三方上传失败')
          );
        }
      },
    });
  };

  /** 触发上传的唯一出口：所有入口都把参数整理成 TpRunOptions 交给它 */
  const triggerWithOptions = (opts: TpRunOptions, confirm?: TpConfirm) =>
    triggerUpload(release.id, {
      // 用户每点一次都是「传这一版」：成功后再次点击也要按新填的目录/文件夹重新
      // 上传并刷新状态，不能被「已成功即跳过」挡住（后端只拦正在执行中的任务）。
      includeSucceeded: true,
      overwrite: opts.overwrite,
      skipExisting: opts.skipExisting,
      targetDir: opts.targetDir,
      authSession: opts.auth?.authSession,
      parentUrl: opts.auth?.parentUrl,
      folder: opts.auth?.folder,
      confirm,
    });

  /**
   * 目标文件夹里已有同名文件：三选一交给用户（后端只做预检，不擅自失败）。
   * 用受控 Modal 而不是 Modal.confirm —— 三个选项，且要确保按钮一定在、不会被
   * 同时关闭的认证弹窗带走。
   */
  const confirmOverwrite = (
    check: ExistingFilesCheck,
    opts: TpRunOptions = {},
    confirm?: TpConfirm,
  ) => {
    setTpConflict({ ...check, opts, confirm });
  };

  /** 用户在同名文件弹窗里做出选择后按选中的策略重新提交 */
  const retryWithPolicy = async (policy: { overwrite?: boolean; skipExisting?: boolean }) => {
    const pending = tpConflict;
    if (!pending) return;
    setTpConflict(null);
    try {
      const res = await triggerWithOptions({ ...pending.opts, ...policy }, pending.confirm);
      reportUploadAccepted(res);
    } catch (e) {
      message.error(errorMessage(e) || t('提交第三方上传失败'));
    }
  };

  /**
   * 真正的触发路径。出错**向上抛**，由调用方决定怎么提示 ——
   * JLR 认证弹窗要把「还没批准」这类错误显示在弹窗里，而不是弹一条 message 后关掉。
   */
  const runThirdPartyUpload = async (opts: TpRunOptions = {}) => {
    // loading 期间按钮不可再点，防止重复触发 RPA 校验（008 FR-022）
    setTpTriggering(true);
    try {
      const res = await triggerWithOptions(opts);
      reportUploadAccepted(res);
    } catch (e) {
      const check = getRpaCheckFromError(e);
      if (check) {
        // RPA 校验未通过：交给确认弹窗，认证会话等参数一并带过去
        confirmRpaCheck(check, opts);
        return;
      }
      const conflicts = getExistingFilesFromError(e);
      if (conflicts) {
        confirmOverwrite(conflicts, opts);
        return;
      }
      throw e;
    } finally {
      setTpTriggering(false);
    }
  };

  /** 直接触发（无弹窗）时的错误提示 */
  const runThirdPartyUploadQuietly = (opts: TpRunOptions = {}) => {
    void runThirdPartyUpload(opts).catch((e) => {
      message.error(errorMessage(e) || t('提交第三方上传失败'));
    });
  };

  const handleThirdPartyUpload = () => {
    // 需要交互认证的插件（JLR SharePoint）：先填账号密码、过 Authenticator 号码匹配。
    if (tpNeedsAuth) {
      setTpAuthOpen(true);
      return;
    }
    // BMW 这类需要目标目录的插件：先问目录再上传。预填 RPA 发布时带过来的值，
    // 用户可改；德赛的可选目录同样走这里，留空即上传到分享根目录（FR-022b）。
    if (tpAsksTargetDir) {
      setTpDirOpen(true);
      return;
    }
    runThirdPartyUploadQuietly();
  };

  const handleTargetDirSubmit = async () => {
    let targetDir: string;
    try {
      const values = await tpDirForm.validateFields();
      targetDir = String(values.target_dir ?? '').trim();
    } catch {
      return; // 表单校验失败，Form 已就地给出提示
    }
    setTpDirSubmitting(true);
    try {
      await runThirdPartyUpload({ targetDir });
    } catch (e) {
      message.error(errorMessage(e) || t('提交第三方上传失败'));
    } finally {
      setTpDirSubmitting(false);
      setTpDirOpen(false);
    }
  };

  /** JLR 认证弹窗的「确认上传」：用户已在手机上批准，带会话去建任务 */
  const handleJlrAuthConfirm = async (auth: JlrAuthPayload) => {
    await runThirdPartyUpload({ auth });
  };

  const handleThirdPartyRetry = async (taskId: number) => {
    try {
      await retryTask(taskId);
      message.success(t('已重新提交上传'));
      tpStartPolling(release.id);
    } catch (e) {
      const detail = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      message.error(detail || t('重试失败'));
    }
  };

  const retryInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const rnRetryInputRef = useRef<HTMLInputElement | null>(null);
  const trRetryInputRef = useRef<HTMLInputElement | null>(null);
  const prevDocStatus = useRef<Record<string, string>>({});
  const uploadRecordIdRef = useRef<number | null>(null);
  // Upload record ID for the current retry operation (separate from main upload record)
  const retryRecordIdRef = useRef<number | null>(null);

  useEffect(() => {
    const docKeys = [
      `externalrelease-${release.id}-doc-release_note`,
      `externalrelease-${release.id}-doc-test_report`,
    ];
    let needRefresh = false;
    for (const key of docKeys) {
      const cur = progress[key]?.status;
      const prev = prevDocStatus.current[key];
      if (prev && prev !== 'done' && cur === 'done') needRefresh = true;
      if (cur !== undefined) prevDocStatus.current[key] = cur;
    }
    if (needRefresh) onRefresh?.();
  }, [progress, release.id, onRefresh]);

  // Fetch upload record ID for retry reporting
  useEffect(() => {
    listUploadRecords({ deliverable_type: 'external_release', deliverable_id: release.id, page_size: 1 })
      .then(res => {
        if (res.results?.length > 0) uploadRecordIdRef.current = res.results[0].id;
      })
      .catch(() => {});
  }, [release.id]);

  const handleDownload = async (fileUrl: string, fileName: string) => {
    await downloadFile(fileUrl, fileName);
  };

  // 外部用户拿到的 download_url 是 bsp_back 的短期签名代理地址（可直接分享）；
  // 内部用户仍用原始 file_url 直连 Artifactory。
  const resolveExternalDownloadUrl = (file: ExternalReleaseFile) => {
    if (!file.download_url) return file.file_url;
    return file.download_url.startsWith('http')
      ? file.download_url
      : `${window.location.origin}${file.download_url}`;
  };

  const handleFileDownload = (file: ExternalReleaseFile) =>
    file.source_type === 'external'
      ? window.open(resolveExternalDownloadUrl(file), '_blank', 'noopener,noreferrer')
      : handleDownload(file.file_url, file.file_name);

  // Artifact 链接由后端解析（EP all_tar_url 或用户直接填写的下载链接），不能走 OBS 签名
  const handleArtifactDownload = (url?: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleArtifactCopyLink = async (url?: string | null) => {
    if (!url) return;
    try {
      await copyToClipboard(url);
      message.success(t('下载链接已复制到剪贴板'));
    } catch {
      message.error(t('复制下载链接失败'));
    }
  };

  const handleFileCopyLink = async (file: ExternalReleaseFile) => {
    if (file.source_type !== 'external') return handleCopyLink(file.file_url, file.file_name);
    await copyToClipboard(resolveExternalDownloadUrl(file));
    message.success(t('下载链接已复制到剪贴板'));
  };

  const handleCopyLink = async (fileUrl: string, fileName: string) => {
    try {
      const link = await getShareableDownloadLink(fileUrl, fileName);
      await copyToClipboard(link);
      message.success(t('下载链接已复制到剪贴板'));
    } catch {
      message.error(t('获取下载链接失败'));
    }
  };

  // Report all current files to upload board (called after retry uploads complete/fail)
  const reportRetryToUploadBoard = (): Promise<void> => {
    if (!uploadRecordIdRef.current) return Promise.resolve();
    const store = useUploadProgressStore.getState();
    const allFiles: UploadedFileItem[] = [];
    let allDone = true;
    let anyFailed = false;

    (release.files || []).forEach(f => {
      const key = `externalrelease-${release.id}-file-${f.id}`;
      const st = store.getProgress(key)?.status || f.upload_status || 'done';
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const e = store.getProgress(key);
      allFiles.push({
        file_name: e?.rawFile?.name ?? f.file_name,
        file_size: e?.rawFile?.size ?? f.file_size,
        file_type_name: f.file_type?.name || '',
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
        change_type: 'added',
      });
    });

    const trPrefix = `externalrelease-${release.id}-doc-test_report`;
    const trKeys = Object.keys(store.progress).filter((k) => k.startsWith(`${trPrefix}-`));
    const trSource = release.momenta_test_report_files?.length
      ? release.momenta_test_report_files
      : release.momenta_test_report_url || release.momenta_test_report_name
        ? [{ name: release.momenta_test_report_name || '', size: release.momenta_test_report_size || 0 }]
        : [];
    const docEntries: { name: string; key: string; realName: string; realSize: number }[] = [
      { name: 'Momenta Release Note', key: `externalrelease-${release.id}-doc-release_note`, realName: release.momenta_release_note_name || '', realSize: release.momenta_release_note_size || 0 },
      ...(trKeys.length > 0
        ? trKeys.map((key, index) => ({
            name: 'Momenta Test Report',
            key,
            realName: trSource[index]?.name || release.momenta_test_report_name || '',
            realSize: trSource[index]?.size || release.momenta_test_report_size || 0,
          }))
        : [{ name: 'Momenta Test Report', key: trPrefix, realName: release.momenta_test_report_name || '', realSize: release.momenta_test_report_size || 0 }]),
    ];
    docEntries.forEach(({ name, key, realName, realSize }) => {
      const st = store.getProgress(key)?.status;
      if (!st) return;
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const e = store.getProgress(key);
      allFiles.push({
        file_name: e?.rawFile?.name ?? (realName || name),
        file_size: e?.rawFile?.size ?? realSize,
        file_type_name: name,
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
        change_type: 'added',
      });
    });

    const status = anyFailed ? 'failed' : allDone ? 'done' : 'uploading';
    return updateUploadRecord(uploadRecordIdRef.current, {
      upload_status: status,
      finished_at: status !== 'uploading' ? new Date().toISOString() : undefined,
      uploaded_files: allFiles,
    }).then(() => {}).catch(() => {});
  };

  // Update the current retry record (retryRecordIdRef) with latest file statuses
  const updateRetryRecord = (retryingFileKey: string): Promise<void> => {
    if (!retryRecordIdRef.current) return Promise.resolve();
    const store = useUploadProgressStore.getState();
    const allFiles: UploadedFileItem[] = [];
    let allDone = true;
    let anyFailed = false;

    (release.files || []).forEach(f => {
      const k = `externalrelease-${release.id}-file-${f.id}`;
      const e = store.getProgress(k);
      const st = e?.status ?? f.upload_status ?? 'done';
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const isRetrying = k === retryingFileKey;
      allFiles.push({
        file_name: e?.rawFile?.name ?? f.file_name,
        file_size: e?.rawFile?.size ?? f.file_size,
        file_type_name: f.file_type?.name || '',
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
        change_type: isRetrying ? (st === 'failed' ? 'failed' : 'added') : (st === 'failed' ? 'failed' : 'existing'),
      });
    });

    const trPrefix = `externalrelease-${release.id}-doc-test_report`;
    const trKeys = Object.keys(store.progress).filter((k) => k.startsWith(`${trPrefix}-`));
    const trSource = release.momenta_test_report_files?.length
      ? release.momenta_test_report_files
      : release.momenta_test_report_url || release.momenta_test_report_name
        ? [{ name: release.momenta_test_report_name || '', size: release.momenta_test_report_size || 0 }]
        : [];
    const docEntries: { name: string; key: string; realName: string; realSize: number }[] = [
      { name: 'Momenta Release Note', key: `externalrelease-${release.id}-doc-release_note`, realName: release.momenta_release_note_name || '', realSize: release.momenta_release_note_size || 0 },
      ...(trKeys.length > 0
        ? trKeys.map((key, index) => ({
            name: 'Momenta Test Report',
            key,
            realName: trSource[index]?.name || release.momenta_test_report_name || '',
            realSize: trSource[index]?.size || release.momenta_test_report_size || 0,
          }))
        : [{ name: 'Momenta Test Report', key: trPrefix, realName: release.momenta_test_report_name || '', realSize: release.momenta_test_report_size || 0 }]),
    ];
    docEntries.forEach(({ name, key, realName, realSize }) => {
      const e = store.getProgress(key);
      if (!e) return;
      const st = e.status ?? 'done';
      if (st === 'uploading' || st === 'queued') { allDone = false; }
      if (st === 'failed') { anyFailed = true; }
      const isRetrying = key === retryingFileKey;
      allFiles.push({
        file_name: e?.rawFile?.name ?? (realName || name),
        file_size: e?.rawFile?.size ?? realSize,
        file_type_name: name,
        auto_retry_count: e?.autoRetryCount ?? 0,
        manual_retry_count: e?.manualRetryCount ?? 0,
        upload_started_at: e?.uploadStartedAt ?? null,
        upload_finished_at: e?.uploadFinishedAt ?? null,
        avg_speed_mbps: e?.avgSpeedMbps ?? null,
        change_type: isRetrying ? (st === 'failed' ? 'failed' : 'added') : (st === 'failed' ? 'failed' : 'existing'),
      });
    });

    const uploadStatus = anyFailed ? 'failed' : allDone ? 'done' : 'uploading';
    return updateUploadRecord(retryRecordIdRef.current, {
      upload_status: uploadStatus,
      finished_at: uploadStatus !== 'uploading' ? new Date().toISOString() : undefined,
      uploaded_files: allFiles,
    }).then(() => {}).catch(() => {});
  };

  const handleRetry = async (file: ExternalReleaseFile, selectedFile: File) => {
    const key = `externalrelease-${release.id}-file-${file.id}`;
    incrementManualRetry(key);
    const startedAt = new Date().toISOString();
    setProgress(key, { percent: 0, status: 'uploading', rawFile: selectedFile, uploadStartedAt: startedAt, uploadFinishedAt: null, avgSpeedMbps: null });

    // 创建重传独立记录
    const store = useUploadProgressStore.getState();
    const initialFiles: UploadedFileItem[] = (release.files || []).map(f => {
      const k = `externalrelease-${release.id}-file-${f.id}`;
      const e = store.getProgress(k);
      const isTarget = f.id === file.id;
      return {
        file_name: isTarget ? selectedFile.name : (e?.rawFile?.name ?? f.file_name),
        file_size: isTarget ? selectedFile.size : (e?.rawFile?.size ?? f.file_size),
        file_type_name: f.file_type?.name || '',
        change_type: isTarget ? 'added' : ((e?.status === 'failed' || f.upload_status === 'failed') ? 'failed' : 'existing'),
      };
    });
    retryRecordIdRef.current = await reportRetryUploadRecord(
      'external_release', release.id, release.project.id,
      { name: release.name, vehicleModel: toVehicleList(release.vehicle_model)[0] ?? '', description: release.description || '' },
      initialFiles, startedAt,
    );

    const startTime = Date.now();
    // 心跳：刷新 updated_at，防止其他 Tab 把正在进行的重传误判为卡住
    let currentPercent = 0;
    const heartbeatTimer = setInterval(() => {
      heartbeatExternalReleaseFile(release.id, file.id, currentPercent).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(selectedFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: selectedFile });
        currentPercent = p.percent;
      }, undefined, () => { incrementAutoRetry(key); }, key);
      if (!result.md5) {
        setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
        await failExternalReleaseFileUpload(release.id, file.id).catch(() => {});
        reportRetryToUploadBoard();
        updateRetryRecord(key);
        return;
      }
      const finishedAt = new Date().toISOString();
      const durationSec = (Date.now() - startTime) / 1000;
      const avgSpeedMbps = durationSec > 0 ? parseFloat((result.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
      setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
      await reportRetryToUploadBoard();
      await updateRetryRecord(key);
      const startedAt = useUploadProgressStore.getState().getProgress(key)?.uploadStartedAt ?? null;
      await completeExternalReleaseFileUpload(release.id, file.id, {
        file_url: result.fileUrl,
        object_key: result.objectKey,
        file_size: result.fileSize,
        checksum: result.md5 || '',
        file_name: selectedFile.name,
        upload_started_at: startedAt,
        upload_finished_at: finishedAt,
        avg_speed_mbps: avgSpeedMbps,
      });
      onRefresh?.();
    } catch {
      setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
      await failExternalReleaseFileUpload(release.id, file.id).catch(() => {});
      reportRetryToUploadBoard();
      updateRetryRecord(key);
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  const handleDocRetry = async (docType: 'momenta_release_note' | 'momenta_test_report', selectedFile: File, trIndex = -1) => {
    const key = docType === 'momenta_test_report'
      ? `externalrelease-${release.id}-doc-test_report-${trIndex >= 0 ? trIndex : 0}`
      : `externalrelease-${release.id}-doc-release_note`;
    incrementManualRetry(key);
    const startedAt = new Date().toISOString();
    setProgress(key, { percent: 0, status: 'uploading', rawFile: selectedFile, uploadStartedAt: startedAt, uploadFinishedAt: null, avgSpeedMbps: null });

    // 创建重传独立记录
    const docTypeName = docType === 'momenta_release_note' ? 'Momenta Release Note' : 'Momenta Test Report';
    const store = useUploadProgressStore.getState();
    const initialFiles: UploadedFileItem[] = (release.files || []).map(f => {
      const k = `externalrelease-${release.id}-file-${f.id}`;
      const e = store.getProgress(k);
      return {
        file_name: e?.rawFile?.name ?? f.file_name,
        file_size: e?.rawFile?.size ?? f.file_size,
        file_type_name: f.file_type?.name || '',
        change_type: (e?.status === 'failed' || f.upload_status === 'failed') ? 'failed' : 'existing',
      };
    });
    initialFiles.push({
      file_name: selectedFile.name,
      file_size: selectedFile.size,
      file_type_name: docTypeName,
      change_type: 'added',
    });
    retryRecordIdRef.current = await reportRetryUploadRecord(
      'external_release', release.id, release.project.id,
      { name: release.name, vehicleModel: toVehicleList(release.vehicle_model)[0] ?? '', description: release.description || '' },
      initialFiles, startedAt,
    );

    const startTime = Date.now();
    // 心跳：刷新 external_release.updated_at，防止其他 Tab 把正在进行的文档重传误判为卡住
    const heartbeatTimer = setInterval(() => {
      patchExternalRelease(release.id, {}).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(selectedFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: selectedFile });
      }, undefined, () => { incrementAutoRetry(key); }, key);
      if (!result.md5) {
        setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
        reportRetryToUploadBoard();
        updateRetryRecord(key);
        return;
      }
      const finishedAt = new Date().toISOString();
      const durationSec = (Date.now() - startTime) / 1000;
      const avgSpeedMbps = durationSec > 0 ? parseFloat((result.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
      setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
      await reportRetryToUploadBoard();
      await updateRetryRecord(key);
      const uploadStartedAt = useUploadProgressStore.getState().getProgress(key)?.uploadStartedAt ?? new Date(Date.now() - durationSec * 1000).toISOString();
      const docTiming = {
        ...release.doc_upload_timing,
        [docType]: { started_at: uploadStartedAt, finished_at: finishedAt, avg_speed_mbps: avgSpeedMbps },
      };
      if (docType === 'momenta_test_report') {
        const base = release.momenta_test_report_files?.length
          ? [...release.momenta_test_report_files]
          : (release.momenta_test_report_url || release.momenta_test_report_name)
            ? [{
                url: release.momenta_test_report_url || '',
                name: release.momenta_test_report_name || '',
                size: release.momenta_test_report_size || 0,
                checksum: release.momenta_test_report_checksum || '',
              }]
            : [];
        const index = trIndex >= 0 ? trIndex : base.findIndex(d => !d.url);
        const entry = {
          url: result.fileUrl,
          name: selectedFile.name,
          size: result.fileSize,
          checksum: result.md5 || '',
        };
        if (index >= 0 && index < base.length) base[index] = entry;
        else base.push(entry);
        await patchExternalRelease(release.id, {
          momenta_test_report_files: base,
          doc_files_merge: true,
          version: release.version,
          doc_upload_timing: docTiming,
        });
      } else {
        await patchExternalRelease(release.id, {
          [`${docType}_url`]: result.fileUrl,
          [`${docType}_name`]: selectedFile.name,
          [`${docType}_size`]: result.fileSize,
          [`${docType}_checksum`]: result.md5 || '',
          version: release.version,
          doc_upload_timing: docTiming,
        });
      }
      onRefresh?.();
    } catch {
      setProgress(key, { percent: 0, status: 'failed', rawFile: selectedFile, uploadFinishedAt: new Date().toISOString() });
      reportRetryToUploadBoard();
      updateRetryRecord(key);
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  const isMd5Missing = (file: ExternalReleaseFile): boolean =>
    file.source_type === 'upload' && !!file.file_url && !file.checksum;

  const renderUploadStatus = (file: ExternalReleaseFile) => {
    const key = `externalrelease-${release.id}-file-${file.id}`;
    const storeEntry = progress[key];
    const effectiveStatus = storeEntry?.status ?? file.upload_status;

    if (!effectiveStatus || effectiveStatus === 'done') {
      if (isMd5Missing(file)) {
        return (
          <Space size={4} style={{ marginTop: 4 }}>
            <Tag color="error">{t('上传失败')}</Tag>
            <input
              type="file"
              style={{ display: 'none' }}
              ref={(el) => { retryInputRefs.current[file.id] = el; }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleRetry(file, f);
                e.target.value = '';
              }}
            />
            <Button
              size="small"
              icon={<ReloadOutlined />}
              disabled={!canWrite}
              onClick={() => retryInputRefs.current[file.id]?.click()}
            >
              {t('选择文件重试')}
            </Button>
          </Space>
        );
      }
      return null;
    }

    if (effectiveStatus === 'uploading') {
      // 优先用本 Tab 的实时进度；跨电脑/跨会话时 storeEntry 为空，回退到服务端持久化的 upload_percent
      const pct = resolveUploadPercent(file, storeEntry);
      const speed = storeEntry?.currentSpeed;
      return (
        <Space orientation="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
          <Progress percent={pct} size="small" status="active" style={{ marginBottom: 0 }} />
          {speed !== undefined && speed > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(speed)}</Text>
          )}
          {!storeEntry && <Text type="secondary" style={{ fontSize: 12 }}>{t('上传中...')}</Text>}
        </Space>
      );
    }

    if (effectiveStatus === 'failed') {
      const savedFile = storeEntry?.rawFile;
      return (
        <Space size={4} style={{ marginTop: 4 }}>
          <Tag color="error">{t('上传失败')}</Tag>
          {savedFile ? (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>{savedFile.name}</Text>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                disabled={!canWrite}
                onClick={() => handleRetry(file, savedFile)}
              >
                {t('重试')}
              </Button>
            </>
          ) : (
            <>
              <input
                type="file"
                style={{ display: 'none' }}
                ref={(el) => { retryInputRefs.current[file.id] = el; }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleRetry(file, f);
                  e.target.value = '';
                }}
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                disabled={!canWrite}
                onClick={() => retryInputRefs.current[file.id]?.click()}
              >
                {t('选择文件重试')}
              </Button>
            </>
          )}
        </Space>
      );
    }

    if (effectiveStatus === 'queued') {
      return <Tag color="processing" style={{ marginTop: 4 }}>{t('排队中')}</Tag>;
    }

    return null;
  };

  /**
   * 整包上传状态（产线包这类「多固件 → 打成一个包 → 传一次」的插件）。
   *
   * 不挂在任何文件行下：整包任务的 file_id 是 null，它描述的是整个产物包。
   * 进度是**真实阶段点**而非连续值（FR-041）：0 → 50（产线包构建完成）→
   * 100（上传完成）。阶段点之间用不确定态进度条，不编造递增数字。
   *
   * 50 这个点只有 `has_build_phase` 的插件才有（立昇/德赛）；BMW 的包由 RPA
   * 构建，进度只有 0 / 100，全程停在 0% —— 文案也要跟着变，见下面的分支。
   */
  const renderPackageUploadStatus = () => {
    if (!tpPackageTask) return null;
    const task = tpPackageTask;

    const retryHint = [
      task.auto_retry_count > 0 ? t('自动重试 {count} 次', { count: task.auto_retry_count }) : '',
      task.manual_retry_count > 0 ? t('手动重试 {count} 次', { count: task.manual_retry_count }) : '',
    ].filter(Boolean).join(lang === 'en' ? ', ' : '，');

    let body: React.ReactNode;
    if (task.status === 'success') {
      body = (
        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
          <div>
            <Tag color="success">{t('{name} 上传成功', { name: task.plugin_name })}</Tag>
            {retryHint && <Text type="secondary" style={{ fontSize: 12 }}>{retryHint}</Text>}
          </div>
          <Progress percent={100} size="small" />
          {task.upload_url && (
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={async () => {
                await copyToClipboard(task.upload_url);
                message.success(t('三方平台下载链接已复制到剪贴板'));
              }}
            >
              {t('三方平台下载链接')}
            </Button>
          )}
        </Space>
      );
    } else if (task.status === 'failed') {
      body = (
        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
          <div>
            <Tag color="error">{t('{name} 上传失败', { name: task.plugin_name })}</Tag>
            {retryHint && <Text type="secondary" style={{ fontSize: 12 }}>{retryHint}</Text>}
            <Button
              size="small"
              type="link"
              icon={<ReloadOutlined />}
              // 交互认证型插件不能「直接重试」：认证会话是一次性的，必须重新点
              // 「上传第三方平台」走一遍号码匹配
              disabled={!canWrite || tpNeedsAuth}
              onClick={() => handleThirdPartyRetry(task.id)}
            >
              {t('重试')}
            </Button>
          </div>
          {task.error_message && (
            <Text type="danger" style={{ fontSize: 12 }}>{task.error_message}</Text>
          )}
        </Space>
      );
    } else {
      // pending / in_progress：progress<50 还在构建，>=50 已构建完、在传。
      //
      // 但「有没有构建阶段」是插件能力，不是所有整包插件都成立：BMW 的包由 RPA
      // 构建完成（FR-003），进度只有 0 / 100 两点（FR-006），永远到不了 50。
      // 若照搬 progress<50，一次 2GB 的下载 + PUT 会整段时间显示「正在构建产线包 /
      // 预计 10-30 分钟」—— 既不对，又让人以为卡住了。
      // 缺省按 true 处理，立昇/德赛的文案与进度条一行不变。
      const builds = tpPlugin?.has_build_phase !== false;
      const built = builds && task.progress >= 50;
      body = (
        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
          <div>
            <Tag color="processing">
              {task.status === 'pending' ? t('排队中')
                : !builds ? t('正在上传{name}', { name: task.plugin_name })
                  : built ? t('产线包已构建，正在上传{name}', { name: task.plugin_name })
                    : t('正在构建产线包')}
            </Tag>
            {builds && !built && task.status !== 'pending' && (
              <Text type="secondary" style={{ fontSize: 12 }}>{t('预计 10-30 分钟')}</Text>
            )}
          </div>
          {/* 有构建阶段的插件只有 0 / 50 / 100 三个真实阶段点（FR-041 不许编造中间值）；
              没有构建阶段的插件（JLR / BMW）由后端按实际传输报 1–99，直接展示即可 ——
              原先这里一律写死 `built ? 50 : 0`，JLR 的进度条就永远是 0%。 */}
          <Progress
            percent={builds ? (built ? 50 : 0) : Math.max(1, Math.min(99, task.progress))}
            size="small"
            status="active"
          />
        </Space>
      );
    }

    // 不加标题：这块紧贴页头显示，上下文已经说明是当前产物包的上传进度，
    // 标题反而占掉一行高度。内部的 Tag 已经写明了阶段与目标平台。
    return (
      <Card size="small" className="mb-4">
        {body}
      </Card>
    );
  };

  /**
   * 逐文件第三方上传状态（FR-024/FR-030/FR-031）。
   *
   * 进度条只展示后端给的 progress，绝不编造中间百分比（FR-041）。逐文件模式
   * 只有 0/100 两档，所以传输中显示不确定态的 0%。
   * （整包模式的 50% 是真实阶段点——产线包构建完成——不走这个分支。）
   */
  const renderThirdPartyStatus = (file: ExternalReleaseFile) => {
    const task = tpTaskByFile.get(file.id);
    if (!task) return null;

    const retryHint = [
      task.auto_retry_count > 0 ? t('自动重试 {count} 次', { count: task.auto_retry_count }) : '',
      task.manual_retry_count > 0 ? t('手动重试 {count} 次', { count: task.manual_retry_count }) : '',
    ].filter(Boolean).join(lang === 'en' ? ', ' : '，');

    if (task.status === 'success') {
      return (
        <div style={{ marginTop: 4 }}>
          <Tag color="success">{t('{name} 上传成功', { name: task.plugin_name })}</Tag>
          {retryHint && <Text type="secondary" style={{ fontSize: 12 }}>{retryHint}</Text>}
        </div>
      );
    }

    if (task.status === 'failed') {
      return (
        <Space orientation="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
          <div>
            <Tag color="error">{t('{name} 上传失败', { name: task.plugin_name })}</Tag>
            {retryHint && <Text type="secondary" style={{ fontSize: 12 }}>{retryHint}</Text>}
            <Button
              size="small"
              type="link"
              icon={<ReloadOutlined />}
              // 交互认证型插件不能「直接重试」：认证会话是一次性的，必须重新点
              // 「上传第三方平台」走一遍号码匹配
              disabled={!canWrite || tpNeedsAuth}
              onClick={() => handleThirdPartyRetry(task.id)}
            >
              {t('重试')}
            </Button>
          </div>
          {task.error_message && (
            <Text type="danger" style={{ fontSize: 12 }}>{task.error_message}</Text>
          )}
        </Space>
      );
    }

    // pending / in_progress
    return (
      <Space orientation="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
        <div>
          <Tag color="processing">
            {task.plugin_name} {task.status === 'pending' ? t('排队中') : t('上传中')}
          </Tag>
          {retryHint && <Text type="secondary" style={{ fontSize: 12 }}>{retryHint}</Text>}
        </div>
        <Progress percent={task.progress} size="small" status="active" style={{ marginBottom: 0 }} />
      </Space>
    );
  };

  const renderFixedFileItem = (file: ExternalReleaseFile) => {
    const isGitSource = file.source_type === 'git';
    const isArtifactSource = file.source_type === 'artifact';
    const key = `externalrelease-${release.id}-file-${file.id}`;
    const storeEntry = progress[key];
    const effectiveStatus = storeEntry?.status ?? file.upload_status;
    const isExternalSource = file.source_type === 'external';
    const flowLabel = [file.flow_name, file.action_name].filter(Boolean).join(' / ');
    const canDownload = isExternalSource
      || (!isGitSource && !isArtifactSource && (!effectiveStatus || effectiveStatus === 'done') && !isMd5Missing(file));

    return (
      <div className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <FileOutlined className="text-2xl text-blue-500" />
            <div>
              <div className="font-medium">
                {isGitSource ? t('{name} (Git源)', { name: file.file_type.name }) : isArtifactSource ? `${file.file_type.name} (Artifact)` : (storeEntry?.rawFile?.name ?? file.file_name)}
              </div>
              <div className="text-sm text-gray-600">
                <Space orientation="vertical" size={4}>
                  <div>
                    <Tag>{file.file_type.name}</Tag>
                    {!isGitSource && !isArtifactSource && !isExternalSource && file.file_size > 0 && (
                      <Text type="secondary" className="ml-2">{formatFileSize(file.file_size)}</Text>
                    )}
                    {!isGitSource && !isArtifactSource && file.upload_started_at && file.upload_finished_at && (
                      <Text type="secondary" className="ml-2" style={{ fontSize: '12px' }}>
                        {formatUploadTiming(file.upload_started_at, file.upload_finished_at, file.avg_speed_mbps)}
                      </Text>
                    )}
                    {isGitSource && <Tag color="cyan">{t('Git源')}</Tag>}
                    {isArtifactSource && <Tag color="purple">Artifact</Tag>}
                    {isExternalSource && (
                      <Tooltip title={t('该文件未存于 BSP，链接由 RPA 提供')}>
                        <Tag color="orange">{t('外部链接')}</Tag>
                      </Tooltip>
                    )}
                    {isExternalSource && file.file_size > 0 && (
                      <Text type="secondary" className="ml-2">{formatFileSize(file.file_size)}</Text>
                    )}
                  </div>
                  {file.original_file_name && (
                    <div>
                      <Text type="secondary">{t('原始文件名:')}</Text>
                      <Text
                        className="ml-2"
                        style={{ fontSize: '12px' }}
                        copyable={{ text: file.original_file_name }}
                      >
                        {file.original_file_name}
                      </Text>
                    </div>
                  )}
                  {isGitSource && file.git_url && (
                    <>
                      <div>
                        <Text type="secondary">{t('Git仓库:')}</Text>
                        <Text copyable className="ml-2" style={{ fontSize: '12px' }}>{file.git_url}</Text>
                      </div>
                      <div>
                        <Text type="secondary">{t('分支:')}</Text>
                        <Text code className="ml-2">{file.git_branch}</Text>
                      </div>
                      <div>
                        <Text type="secondary">Commit:</Text>
                        <Text code copyable className="ml-2">{file.git_commit}</Text>
                      </div>
                      {file.git_tag && (
                        <div>
                          <Text type="secondary">Tag:</Text>
                          <Text code className="ml-2">{file.git_tag}</Text>
                        </div>
                      )}
                    </>
                  )}
                  {isArtifactSource && file.artifact_name && (
                    <div>
                      <Text type="secondary">{t('产物包名称:')}</Text>
                      <Text copyable className="ml-2">{file.artifact_name}</Text>
                    </div>
                  )}
                  {!isGitSource && !isArtifactSource && file.checksum && (
                    <Text
                      type="secondary"
                      copyable={{ text: file.checksum }}
                      title={file.checksum}
                      style={{ fontSize: '12px' }}
                    >
                      MD5: {file.checksum}
                    </Text>
                  )}
                  {flowLabel && (
                    <div>
                      <Text type="secondary">Pipeline action name:</Text>
                      <Text className="ml-2" style={{ fontSize: '12px' }}>{flowLabel}</Text>
                    </div>
                  )}
                  {file.comment && (
                    <div>
                      <Text type="secondary">{t('备注:')}</Text>
                      <Text className="ml-2" style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                        {file.comment}
                      </Text>
                    </div>
                  )}
                  {!isGitSource && !isArtifactSource && renderUploadStatus(file)}
                  {!isGitSource && !isArtifactSource && renderThirdPartyStatus(file)}
                </Space>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isArtifactSource ? (
            <Tooltip title={file.artifact_download_url ? undefined : t('未查询到产物包下载链接')}>
              <span>
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  disabled={!file.artifact_download_url}
                  onClick={() => handleArtifactDownload(file.artifact_download_url)}
                >
                  {t('下载')}
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  disabled={!file.artifact_download_url}
                  onClick={() => handleArtifactCopyLink(file.artifact_download_url)}
                >
                  {t('复制下载链接')}
                </Button>
              </span>
            </Tooltip>
          ) : !isGitSource && (
            <>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                disabled={!canDownload}
                onClick={() => canDownload && handleFileDownload(file)}
              >
                {t('下载')}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                disabled={!canDownload}
                onClick={() => canDownload && handleFileCopyLink(file)}
              >
                {t('下载链接')}
              </Button>
              {/* 这里曾有「三方平台下载链接」按钮（逐文件上传时每个固件各有一个
                  平台侧地址）。整包模式下多个固件打成一个包、只产生一条链接，
                  逐文件链接恒为空、按钮永远置灰，已移除。整包链接在上方的整包
                  上传区展示。 */}
            </>
          )}
        </div>
      </div>
    );
  };

  type DocFileEntry = { name?: string; url?: string; size?: number; checksum?: string };

  const renderDocSection = (
    files: DocFileEntry[],
    content: string | undefined,
    label: string,
    tagColor: string,
    timing?: { started_at: string; finished_at: string; avg_speed_mbps?: number | null },
  ) => {
    if (files.length === 0 && !content) return null;
    const dataSource = files;

    return (
      <div className="mb-4">
        <div className="font-medium mb-2">{label}</div>
        {dataSource.length > 0 && (
          <div className={content ? 'mb-4' : ''}>
            {content && <div className="text-gray-600 text-sm mb-1">{t('附件:')}</div>}
            <div>
              {dataSource.map((file, idx) => (
                <div key={idx} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <FileOutlined className="text-2xl text-green-500" />
                      <div>
                        <div className="font-medium">{file.name}</div>
                        <div className="text-sm text-gray-600">
                          <Space orientation="vertical" size={4}>
                            <Space>
                              <Tag color={tagColor}>{label}</Tag>
                              {file.size && (
                                <Text type="secondary" className="text-xs">
                                  {formatFileSize(file.size)}
                                </Text>
                              )}
                              {idx === 0 && timing?.started_at && timing?.finished_at && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {formatUploadTiming(timing.started_at, timing.finished_at, timing.avg_speed_mbps)}
                                </Text>
                              )}
                            </Space>
                            {file.checksum && (
                              <Text type="secondary" copyable={{ text: file.checksum }} style={{ fontSize: '12px' }}>
                                MD5: {file.checksum}
                              </Text>
                            )}
                          </Space>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(file.url!, file.name!)}
                    >
                      {t('下载')}
                    </Button>
                    <Button
                      type="link"
                      icon={<CopyOutlined />}
                      onClick={() => handleCopyLink(file.url!, file.name!)}
                    >
                      {t('复制下载链接')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {content && (
          <div>
            {files.length > 0 && <div className="text-gray-600 text-sm mb-1">{t('内容:')}</div>}
            <FeishuRichText
              content={content}
              titles={feishuTitles}
              loading={feishuLoading}
              className="whitespace-pre-wrap p-4 bg-gray-50 rounded"
            />
          </div>
        )}
      </div>
    );
  };

  const fixedFiles = release.files || [];

  return (
    <div className="external-release-detail">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-1">
          <Title level={4} className="mb-0">
            {release.name}
          </Title>
          <CopyExternalLinkButton
            id={release.id}
            kind="release"
            projectId={release.project?.id}
          />
        </div>
        <Space>
          <Button icon={<HistoryOutlined />} onClick={onViewLogs}>
            {t('操作日志')}
          </Button>
          {/* 未配插件/包未完成/上传中三种情形置灰，悬浮给出具体原因（FR-016/FR-017） */}
          <Tooltip title={!canWrite ? t('外部用户无编辑权限') : tpDisabledReason}>
            <Button
              icon={<CloudUploadOutlined />}
              loading={tpTriggering}
              disabled={!canWrite || !!tpDisabledReason}
              onClick={handleThirdPartyUpload}
            >
              {t('上传第三方平台')}
            </Button>
          </Tooltip>
          {/* 上传中禁止编辑：源文件被替换会让推到第三方的内容新旧混杂（FR-021） */}
          <Tooltip title={!canWrite ? t('外部用户无编辑权限') : tpEditable ? '' : t('正在上传第三方平台，完成后才能编辑')}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={onEdit}
              disabled={!canWrite || !tpEditable}
            >
              {t('编辑')}
            </Button>
          </Tooltip>
        </Space>
      </div>

      {/* 整包上传进度：紧跟页头、置于基本信息之上。这是页面上唯一会自己变化的
          内容，放在最下面用户看不到（要滚过整个文件列表）。仅整包模式的插件
          会产生这条任务，没有时不渲染、不占位。 */}
      {renderPackageUploadStatus()}

      {/* 基本信息 */}
      <Card className="mb-4">
        <Descriptions title={t('基本信息')} column={2} bordered size="small">
          <Descriptions.Item label={t('名称')}>{release.name}</Descriptions.Item>
          <Descriptions.Item label={t('版本号')}>{release.version}</Descriptions.Item>
          {/* RPA 显示原文；BSP 记录显示所选类型，未选择显示「-」 */}
          <Descriptions.Item label={t('版本类型')}>
            {release.source === 'rpa' ? (
              <Tag>{release.rpa_version_type || '-'}</Tag>
            ) : release.version_type ? (
              <Tag color={release.version_type === 'official' ? 'green' : undefined}>
                {versionTypeLabel(release.version_type, t)}
              </Tag>
            ) : (
              <span className="text-gray-600">-</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('释放目的')}>
            <Tag color={release.release_purpose === 'TEST' ? 'orange' : 'green'}>
              {releasePurposeLabel(release.release_purpose, t)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('项目')}>
            <Tag color="blue">{release.project?.name}</Tag>
          </Descriptions.Item>
          {release.source === 'rpa' && (
            <Descriptions.Item label={t('来源')} span={2}>
              <Space size={[4, 4]} wrap>
                <Tag color="blue">RPA</Tag>
                {release.rpa_version_valid
                  ? <Tag color="green">{t('版本校验通过')}</Tag>
                  : <Tag color="red">{t('版本校验未通过')}</Tag>}
                {/* 实际触发上传需版本校验通过且上传决策为真，两个条件缺一即仅建记录 */}
                <Tag>
                  {release.rpa_auto_upload && release.rpa_version_valid ? t('自动上传') : t('仅建记录')}
                </Tag>
                <Text type="secondary" copyable={{ text: release.rpa_request_id ?? '' }}>
                  request_id: {release.rpa_request_id}
                </Text>
              </Space>
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('车型')}>{formatVehicleList(release.vehicle_model, lang)}</Descriptions.Item>
          <Descriptions.Item label={t('子车型')}>{formatVehicleList(release.vehicle_subtype, lang)}</Descriptions.Item>
          <Descriptions.Item label={t('硬件平台')}>{release.platform || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('说明')}>{release.description || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('上传者')}>
            {release.created_by?.username}
          </Descriptions.Item>
          <Descriptions.Item label={t('创建时间')}>
            {formatDate(release.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t('更新时间')}>
            {formatDate(release.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t('上传模式')}>
            {formatUploadMode(release.upload_mode)}
          </Descriptions.Item>
          <Descriptions.Item label={t('分片大小')}>
            {formatChunkSize(release.chunk_size)}
          </Descriptions.Item>
          <Descriptions.Item label={t('关联文件')} span={2}>
            {release.packages?.length ? (
              <Space size={[4, 4]} wrap>
                {release.packages.map((p) => (
                  <Tag key={p.id} color="blue">{p.name}</Tag>
                ))}
              </Space>
            ) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Momenta释放文件 */}
      <Card title={t('Momenta释放文件')} className="mb-4">
        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          <span style={{ fontWeight: 'bold' }}>{t('固定文件')}</span>
        </Divider>
        {fixedFiles.length > 0 ? (
          <div>{fixedFiles.map((file) => <div key={file.id}>{renderFixedFileItem(file)}</div>)}</div>
        ) : (
          <Text type="secondary">{t('暂无固定文件')}</Text>
        )}

        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          <span style={{ fontWeight: 'bold' }}>{t('文档文件')}</span>
        </Divider>
        {(() => {
          const rnKey = `externalrelease-${release.id}-doc-release_note`;
          const rnEntry = progress[rnKey];
          const rnStuck = !rnEntry && release.momenta_release_note_upload_status === 'failed';
          if ((rnEntry && rnEntry.status !== 'done') || rnStuck) {
            const rnStatus = rnStuck ? 'failed' : rnEntry!.status;
            const rnPercent = rnStuck ? 0 : rnEntry!.percent;
            const rnFileName = rnEntry?.rawFile?.name ?? (rnStuck ? release.momenta_release_note_name : undefined);
            return (
              <div className="mb-4">
                <div className="font-medium mb-2">Momenta Release Note</div>
                <Space orientation="vertical" size={2} style={{ maxWidth: 400 }}>
                  {rnFileName && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{rnFileName}</Text>
                  )}
                  {!rnStuck && rnEntry!.status === 'queued' && (
                    <Tag color="processing">{t('排队中')}</Tag>
                  )}
                  {rnStatus !== 'failed' && rnStatus !== 'queued' && (
                    <Progress
                      percent={rnPercent}
                      size="small"
                      status="active"
                      style={{ marginBottom: 0 }}
                    />
                  )}
                  {rnStatus === 'failed' && (
                    <>
                      <input
                        type="file"
                        style={{ display: 'none' }}
                        ref={rnRetryInputRef}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleDocRetry('momenta_release_note', f);
                          e.target.value = '';
                        }}
                      />
                      <Space size={4}>
                        <Tag color="error">{t('上传失败')}</Tag>
                        {rnEntry?.rawFile && !rnStuck ? (
                          <Button size="small" icon={<ReloadOutlined />} disabled={!canWrite} onClick={() => handleDocRetry('momenta_release_note', rnEntry!.rawFile!)}>{t('重试')}</Button>
                        ) : (
                          <Button size="small" icon={<ReloadOutlined />} disabled={!canWrite} onClick={() => rnRetryInputRef.current?.click()}>{t('选择文件重试')}</Button>
                        )}
                      </Space>
                    </>
                  )}
                  {!rnStuck && rnStatus !== 'failed' && rnEntry!.currentSpeed !== undefined && rnEntry!.currentSpeed > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(rnEntry!.currentSpeed)}</Text>
                  )}
                  {!rnStuck && rnEntry!.status === 'uploading' && (!rnEntry!.currentSpeed || rnEntry!.currentSpeed === 0) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('上传中...')}</Text>
                  )}
                </Space>
                {release.momenta_release_note_content && (
                  <div className="mt-3">
                    <div className="text-gray-600 text-sm mb-1">{t('内容:')}</div>
                    <FeishuRichText
                      content={release.momenta_release_note_content}
                      titles={feishuTitles}
                      loading={feishuLoading}
                      className="whitespace-pre-wrap p-4 bg-gray-50 rounded"
                    />
                  </div>
                )}
              </div>
            );
          }
          return renderDocSection(
            release.momenta_release_note_url
              ? [{
                  name: release.momenta_release_note_name,
                  url: release.momenta_release_note_url,
                  size: release.momenta_release_note_size,
                  checksum: release.momenta_release_note_checksum,
                }]
              : [],
            release.momenta_release_note_content,
            'Momenta Release Note',
            'purple',
            release.doc_upload_timing?.momenta_release_note,
          );
        })()}
        {(() => {
          const prefix = `externalrelease-${release.id}-doc-test_report`;
          const allTrDocs = release.momenta_test_report_files?.length
            ? release.momenta_test_report_files.map(d => ({ name: d.name, url: d.url, size: d.size, checksum: d.checksum }))
            : (release.momenta_test_report_url || release.momenta_test_report_name)
              ? [{
                  name: release.momenta_test_report_name,
                  url: release.momenta_test_report_url,
                  size: release.momenta_test_report_size,
                  checksum: release.momenta_test_report_checksum,
                }]
              : [];
          const trDocs = allTrDocs.filter(d => d.url);
          const pendingRows = new Map<string, string | undefined>();
          allTrDocs.forEach((d, index) => {
            if (!d.url) pendingRows.set(`${prefix}-${index}`, d.name);
          });
          Object.keys(progress).forEach((k) => {
            if (k !== prefix && !k.startsWith(`${prefix}-`)) return;
            if (!progress[k] || progress[k].status === 'done') return;
            if (!pendingRows.has(k)) pendingRows.set(k, undefined);
          });
          const legacyStuck = pendingRows.size === 0 && release.momenta_test_report_upload_status === 'failed';
          const content = release.momenta_test_report_content;
          if (!trDocs.length && pendingRows.size === 0 && !legacyStuck && !content) return null;

          const renderProgress = (key: string, name: string | undefined, failed: boolean, rawFile?: File) => {
            const entry = progress[key];
            const status = entry?.status ?? (failed ? 'failed' : 'uploading');
            const retry = (file: File) => {
              const index = key === prefix ? -1 : Number(key.slice(prefix.length + 1));
              handleDocRetry('momenta_test_report', file, Number.isInteger(index) ? index : -1);
            };
            return (
              <div key={key} className="mb-2">
                <Space orientation="vertical" size={2} style={{ maxWidth: 400 }}>
                  {name && <Text type="secondary" style={{ fontSize: 12 }}>{name}</Text>}
                  {status === 'queued' && <Tag color="processing">{t('排队中')}</Tag>}
                  {status !== 'failed' && status !== 'queued' && (
                    <Progress percent={entry?.percent ?? 0} size="small" status="active" style={{ marginBottom: 0 }} />
                  )}
                  {status === 'failed' && (
                    <Space size={4}>
                      <Tag color="error">{t('上传失败')}</Tag>
                      {rawFile ? (
                        <Button size="small" icon={<ReloadOutlined />} disabled={!canWrite} onClick={() => retry(rawFile)}>{t('重试')}</Button>
                      ) : (
                        <>
                          <input
                            type="file"
                            style={{ display: 'none' }}
                            ref={trRetryInputRef}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) retry(f);
                              e.target.value = '';
                            }}
                          />
                          <Button size="small" icon={<ReloadOutlined />} disabled={!canWrite} onClick={() => trRetryInputRef.current?.click()}>{t('选择文件重试')}</Button>
                        </>
                      )}
                    </Space>
                  )}
                  {entry && status !== 'failed' && entry.currentSpeed !== undefined && entry.currentSpeed > 0 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{formatSpeed(entry.currentSpeed)}</Text>
                  )}
                  {status === 'uploading' && (!entry?.currentSpeed || entry.currentSpeed === 0) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('上传中...')}</Text>
                  )}
                </Space>
              </div>
            );
          };

          return (
            <>
              {renderDocSection(trDocs, content, t('Momenta 测试报告'), 'purple', release.doc_upload_timing?.momenta_test_report)}
              {Array.from(pendingRows.entries()).map(([key, pendingName]) => {
                const index = key === prefix ? -1 : Number(key.slice(prefix.length + 1));
                const entry = progress[key];
                const name = entry?.rawFile?.name
                  ?? pendingName
                  ?? (Number.isInteger(index) && index >= 0 ? allTrDocs[index]?.name : release.momenta_test_report_name);
                const failed = entry ? entry.status === 'failed' : release.momenta_test_report_upload_status === 'failed';
                return renderProgress(key, name, failed, entry?.rawFile);
              })}
              {legacyStuck && renderProgress(prefix, release.momenta_test_report_name, true)}
            </>
          );
        })()}
        {!release.momenta_release_note_url &&
          !release.momenta_release_note_content &&
          !(release.momenta_test_report_files?.length) &&
          !release.momenta_test_report_url &&
          !release.momenta_test_report_content &&
          !progress[`externalrelease-${release.id}-doc-release_note`] &&
          !Object.keys(progress).some((k) => k.startsWith(`externalrelease-${release.id}-doc-test_report`)) &&
          !release.momenta_release_note_name &&
          !release.momenta_test_report_name && (
            <Text type="secondary">{t('暂无文档文件')}</Text>
          )}
      </Card>

      <div className="flex justify-end">
        <Button onClick={onClose}>{t('关闭')}</Button>
      </div>

      {/* 目标目录输入（Feature 009）。destroyOnClose + initialValues 保证每次打开
          都从最新的 release.upload_target_dir 取值，而不是上次的残留。 */}
      <Modal
        title={t('填写第三方制品库目标目录')}
        open={tpDirOpen}
        onOk={handleTargetDirSubmit}
        onCancel={() => setTpDirOpen(false)}
        confirmLoading={tpDirSubmitting}
        okText={t('上传')}
        cancelText={t('取消')}
        destroyOnClose
        width={600}
      >
        <Form
          form={tpDirForm}
          layout="vertical"
          autoComplete="off"
          preserve={false}
          initialValues={{ target_dir: release.upload_target_dir || '' }}
        >
          <Form.Item
            label={t('目标目录')}
            name="target_dir"
            rules={[
              ...(tpNeedsTargetDir
                ? [{ required: true, whitespace: true, message: t('请填写第三方制品库的目标目录') }]
                : []),
              { max: 500, message: t('目标目录长度不能超过 500 个字符') },
            ]}
            extra={
              tpNeedsTargetDir
                ? t('发布请求已带上目录时这里会自动填好，可直接修改。')
                : t('可留空：留空表示上传到分享根目录（如 AY5T 可填 OTA5）。')
            }
          >
            <Input placeholder={t('例如：OTA5')} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* JLR SharePoint：账号密码 → Authenticator 号码匹配 → 确认上传 */}
      {tpNeedsAuth && (
        <JlrAuthModal
          open={tpAuthOpen}
          releaseId={release.id}
          onConfirm={handleJlrAuthConfirm}
          onCancel={() => setTpAuthOpen(false)}
        />
      )}

      {/* 同名文件：三选一。用受控 Modal 而不是 Modal.confirm —— 三个选项，
          且要保证按钮一定在、不会跟着认证弹窗一起消失。 */}
      <Modal
        title={t('目标文件夹已存在同名文件')}
        open={!!tpConflict}
        width={600}
        maskClosable={false}
        onCancel={() => setTpConflict(null)}
        footer={[
          <Button key="cancel" onClick={() => setTpConflict(null)}>
            {t('取消本次上传')}
          </Button>,
          <Button
            key="skip"
            disabled={
              !!tpConflict && tpConflict.total > 0
              && tpConflict.conflicts.length >= tpConflict.total
            }
            onClick={() => void retryWithPolicy({ skipExisting: true })}
            title={
              tpConflict && tpConflict.total > 0
              && tpConflict.conflicts.length >= tpConflict.total
                ? t('所有文件都已存在，没有可上传的文件')
                : t('已存在的跳过，其余照常上传')
            }
          >
            {t('只上传不同名的文件')}
          </Button>,
          <Button
            key="overwrite"
            type="primary"
            danger
            onClick={() => void retryWithPolicy({ overwrite: true })}
          >
            {t('覆盖')}
          </Button>,
        ]}
      >
        {tpConflict && (
          <div>
            {tpConflict.conflicts.length === 0 ? (
              // 后端只给了错误码、没给清单时也要让用户能选
              <p>{tpConflict.message || t('目标文件夹已存在同名文件。')}</p>
            ) : (
              <p>
                {t('本次共 {total} 个文件，其中', { total: tpConflict.total })}{' '}
                <Text strong>{tpConflict.conflicts.length}</Text> {t('个已存在于目标文件夹：')}
              </p>
            )}
            <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
              {tpConflict.conflicts.map((c) => (
                <li key={c.name}>
                  <Text code>{c.name}</Text>{' '}
                  {c.same_size === true && <Text type="secondary">{t('（大小一致）')}</Text>}
                  {c.same_size === false && (
                    <Text type="warning">
                      {t('（大小不同：平台 {remote} → 本次 {local}）', { remote: formatFileSize(c.remote_size), local: formatFileSize(c.local_size) })}
                    </Text>
                  )}
                  {c.same_size === null && <Text type="secondary">{t('（大小未知）')}</Text>}
                </li>
              ))}
            </ul>
            <p style={{ marginBottom: 0 }}>
              {t('选择「覆盖」会替换平台上的原文件；选择「只上传不同名的文件」则保留原文件，只传其余的；选择「取消本次上传」则一个文件都不传。')}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExternalReleaseDetail;
